import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Star, MapPin, Phone, Globe } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";

export default function BusinessCard({ business }) {
  const { t } = useI18n();
  const isHighlight = business.featured || business.sponsored;
  return (
    <Card
      className={`overflow-hidden border-stone-200 bg-white h-full flex flex-col transition-all duration-300 hover:-translate-y-1 ${isHighlight ? "featured-glow border-[var(--brand-primary)]/30" : "hover:shadow-md"}`}
      data-testid={`business-card-${business.id}`}
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-stone-100">
        {business.image_url && (
          <img
            src={business.image_url}
            alt={business.name}
            className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
            loading="lazy"
          />
        )}
        <div className="absolute top-3 end-3 flex flex-col gap-1.5 items-end">
          {business.featured && (
            <Badge className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary)] text-white text-[10px] tracking-widest uppercase rounded-md" data-testid="featured-badge">
              {t("featured")}
            </Badge>
          )}
          {business.sponsored && (
            <Badge className="bg-[var(--brand-accent)] hover:bg-[var(--brand-accent)] text-stone-900 text-[10px] tracking-widest uppercase rounded-md" data-testid="sponsored-badge">
              {t("sponsored")}
            </Badge>
          )}
        </div>
      </div>
      <CardContent className="p-5 flex flex-col gap-3 flex-1">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-display text-xl text-stone-900 leading-tight">{business.name}</h3>
          {business.rating > 0 && (
            <span className="inline-flex items-center gap-1 text-sm text-stone-700">
              <Star className="h-3.5 w-3.5 text-[var(--brand-accent)] fill-[var(--brand-accent)]" /> {business.rating.toFixed(1)}
            </span>
          )}
        </div>
        <p className="text-sm text-stone-600 leading-relaxed line-clamp-3">{business.description}</p>
        <div className="mt-auto pt-3 border-t border-stone-100 flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-stone-500">
          {business.address && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {business.address}</span>}
          {business.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {business.phone}</span>}
          {business.website && (
            <a href={business.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-[var(--brand-primary)]">
              <Globe className="h-3 w-3" /> Website
            </a>
          )}
        </div>
        {business.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {business.tags.slice(0, 4).map((tag) => (
              <span key={tag} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-stone-100 text-stone-600">{tag}</span>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
